'use client';

import { useMemo } from 'react';
import { MaskedText, SectionHeader } from '@/components/ui';
import { IconCrane, IconScrewdriverWrench, IconChevronRight } from '@repo/svgs';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import { cn } from '@/lib/cn';
import { ConstructionHistoryContent } from './ConstructionHistoryModal';
import { useAnalytics } from '@/hooks/useAnalytics';

interface ConstructionSectionProps {
  buildingAddress: string;
  isBlurRequired?: boolean;
  baseConstructionData?: any | null;
}

// Calculate total count from construction data
const calculateTotalCount = (data?: Record<string, any[]>): number => {
  if (!data) return 0;
  let total = 0;
  for (const key in data) {
    if (Array.isArray(data[key])) {
      total += data[key].length;
    }
  }
  return total;
};

export function ConstructionSection({
  buildingAddress,
  isBlurRequired = false,
  baseConstructionData,
}: ConstructionSectionProps) {
  const analytics = useAnalytics();
  // Transform API response to component format
  const { majorCount, minorCount, majorConstruction, minorConstruction } = useMemo(() => {
    if (!baseConstructionData) {
      return { majorCount: 0, minorCount: 0, majorConstruction: {}, minorConstruction: {} };
    }

    const constructions =
      baseConstructionData.constructions ||
      baseConstructionData.construction ||
      baseConstructionData;
    const major = constructions.major_construction || constructions.majorConstruction || {};
    const minor = constructions.minor_construction || constructions.minorConstruction || {};

    const majorTotal = calculateTotalCount(major);
    const minorTotal = calculateTotalCount(minor);

    return {
      majorCount: majorTotal,
      minorCount: minorTotal,
      majorConstruction: major,
      minorConstruction: minor,
    };
  }, [baseConstructionData]);

  // Hardcoded descriptions for Major and Minor projects
  const majorDescription =
    'New building construction, conversion of commercial to residential use, adding additional floors';
  const minorDescription =
    'Temporary fences, scaffolding, interior demolition, unit renovations, curb cuts that affect exterior aesthetics';

  return (
    <div>
      <SectionHeader
        title={`Construction near ${buildingAddress}`}
        subtitleText="City data for ongoing and upcoming construction projects"
      />

      <div className="mt-4 space-y-0">
        {/* Major Projects */}
        <ModalTrigger
          label="Major construction"
          content={() => (
            <ConstructionHistoryContent constructionData={majorConstruction} isMajor={true} />
          )}
          maxWidth="md"
          disabled={majorCount === 0 || isBlurRequired}
          onOpen={() => analytics.track('rentalreport_construction_majorview')}
          onClose={() => analytics.track('rentalreport_construction_majorview_exit')}
        >
          <div
            className={cn(
              'flex items-start gap-4 py-3 md:px-4 px-2 rounded-lg transition-colors',
              majorCount > 0 && !isBlurRequired ? 'hover:bg-secondary cursor-pointer' : '',
            )}
          >
            <IconCrane className="w-6 h-6 text-caution-mid shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-1">
                  {isBlurRequired ? (
                    <>
                      <MaskedText className="h-4 !min-w-4" />
                      <span className="text-body-2 text-primary">Major projects</span>
                    </>
                  ) : (
                    <>
                      <span className="text-body-1-high text-primary">{majorCount}</span>
                      <span className="text-body-2 text-primary">
                        Major {majorCount === 1 ? 'project' : 'projects'}
                      </span>
                    </>
                  )}
                </div>
                {majorCount > 0 && !isBlurRequired && (
                  <IconChevronRight className="w-3 h-3 text-tertiary shrink-0" />
                )}
              </div>
              <p className="text-caption text-secondary">{majorDescription}</p>
            </div>
          </div>
        </ModalTrigger>

        <div className="border-b border-tertiary my-1 md:mx-4 mx-2" />

        {/* Minor Projects */}
        <ModalTrigger
          label="Minor construction"
          content={() => (
            <ConstructionHistoryContent constructionData={minorConstruction} isMajor={false} />
          )}
          maxWidth="md"
          disabled={minorCount === 0 || isBlurRequired}
          onOpen={() => analytics.track('rentalreport_construction_minorview')}
          onClose={() => analytics.track('rentalreport_construction_minorview_exit')}
        >
          <div
            className={cn(
              'flex items-start gap-4 py-3 md:px-4 px-2 rounded-lg transition-colors',
              minorCount > 0 && !isBlurRequired ? 'hover:bg-secondary cursor-pointer' : '',
            )}
          >
            <IconScrewdriverWrench className="w-6 h-6 text-caution shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-1">
                  {isBlurRequired ? (
                    <>
                      <MaskedText className="h-4 !min-w-4" />
                      <span className="text-body-2 text-primary">Minor projects</span>
                    </>
                  ) : (
                    <>
                      <span className="text-body-1-high text-primary">{minorCount}</span>
                      <span className="text-body-2 text-primary">
                        Minor {minorCount === 1 ? 'project' : 'projects'}
                      </span>
                    </>
                  )}
                </div>
                {minorCount > 0 && !isBlurRequired && (
                  <IconChevronRight className="w-3 h-3 text-tertiary shrink-0" />
                )}
              </div>
              <p className="text-caption text-secondary">{minorDescription}</p>
            </div>
          </div>
        </ModalTrigger>
      </div>
    </div>
  );
}
